import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { useCustomerOrders } from '../../orders/ordersApi.js';
import { ordersLog } from '../logging.js';
import { resolveUnifiedOrdersLoadMessageKey } from '../presenters/unifiedOrdersLoadError.js';

export type AccountOrdersScreenPhase = 'loading' | 'error' | 'empty' | 'populated';

export function formatCollectTimingLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  timing: 'NOW' | 'LATER' | null | undefined,
): string {
  if (timing === 'LATER') {
    return t('account.orders.collectLater');
  }
  if (timing === 'NOW') {
    return t('account.orders.collectNow');
  }
  return t('account.orders.collectUnknown');
}

export function useAccountOrdersScreen() {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const query = useCustomerOrders();

  useEffect(() => {
    if (!query.isError) {
      return;
    }
    ordersLog.error('Account orders load failed', query.error, {
      operation: 'listOrders',
      tenantCode,
    });
  }, [query.error, query.isError, tenantCode]);

  let phase: AccountOrdersScreenPhase = 'loading';
  if (!query.isLoading) {
    if (query.error instanceof Error) {
      phase = 'error';
    } else if ((query.data?.items.length ?? 0) === 0) {
      phase = 'empty';
    } else {
      phase = 'populated';
    }
  }

  const errorMessage =
    query.error instanceof Error
      ? t(resolveUnifiedOrdersLoadMessageKey(query.error))
      : '';

  return {
    t,
    tenantCode,
    phase,
    errorMessage,
    items: query.data?.items ?? [],
    formatCollectTiming: (timing: 'NOW' | 'LATER' | null | undefined) =>
      formatCollectTimingLabel(t, timing),
  };
}
